import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { http, unwrap } from './client'
import type { ApiEnvelope, AppUser, Role } from '@/types/api'

export const userKeys = {
  all: ['admin', 'users'] as const,
}

export const useUsers = () =>
  useQuery({
    queryKey: userKeys.all,
    queryFn: async () => {
      const { data } = await http.get<ApiEnvelope<AppUser[]>>('/admin/users')
      return unwrap(data)
    },
  })

export const useCreateUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      name: string
      email: string
      role: Role
      function?: string
      preferredName?: string
    }) => {
      const { data } = await http.post<ApiEnvelope<AppUser>>('/admin/users', payload)
      return unwrap(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      changes,
    }: {
      id: string
      changes: Partial<{
        name: string
          role: Role
        status: 'active' | 'inactive'
        function: string
        preferredName: string
      }>
    }) => {
      const { data } = await http.put<ApiEnvelope<AppUser>>(`/admin/users/${id}`, changes)
      return unwrap(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  })
}

export const useDeactivateUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await http.delete<ApiEnvelope<AppUser>>(`/admin/users/${id}`)
      return unwrap(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  })
}
